// R5.2 — THE EVIDENCE LOOKUP (spec §4.2, §4.1, §4.3, §4.6).
//
// The payload cites evidence by ID and never by text: `entries[].{facet}.evidence_fact_ids`,
// `domains[].evidence_fact_ids`, `presentation.sub_elements[].evidence_layout_fact_ids`,
// `recommendations[].evidence.fact_ids`, `recommendations[].slots.*.fact_id`. §1.2b types every one of those
// paths `evidence_reference`: "a fact/entry/unit id pointer, not itself display text". This module is the ONE
// place those pointers become text, and the text it produces is the applicant's own words — rendered
// `applicant_verbatim`, per the provenance map's resolution-side rule.
//
// THE MECHANISM IS THE EXISTING ONE, NOT A NEW ONE (§4.2, closing R5-8P-02): id → fact → `EvidenceSpan`, over
// `GET /api/fact-ledger/:cv_id`'s own response. Nothing here re-materializes, re-derives, or re-bands anything.
//
// THE FOUR ID SHAPES, and where each is minted server-side — mirrored here, never invented:
//   * entry-level facts        `entries[].facts[].fact_id`      (`factLedger.ts` builds them; the engine's
//                                                                `factIdFor` looks them up by field)
//   * work status facts        `${work_id}:status:${i}`         (`validateWorkEvidence`)
//   * work dissemination facts `${work_id}:event:${i}`          (`validateWorkEvidence`)
//   * layout facts             `layout_facts[].layout_fact_id`  (the presentation layer's own citations)
//
// AN UNRESOLVED ID RESOLVES TO NOTHING, and the affordance that cited it does not render (`EvidenceReveal`
// returns null on an empty list). It never renders a placeholder, a fabricated span, or the id itself: a "show
// source" that opens onto something we cannot evidence is worse than no affordance at all.
import type { ResolvedEvidence } from './renderContent';

/** The provenance union as the ledger API serializes it — the §2.1 shape, narrowed to what a reveal needs. */
interface LedgerFactLike {
  fact_id?: unknown;
  field?: unknown;
  provenance?: unknown;
  evidence_span?: { quoted_text?: unknown } | null;
  user_value?: unknown;
}

interface EvidenceCarrier {
  evidence?: { provenance?: unknown; evidence_span?: { quoted_text?: unknown } | null; user_value?: unknown } | null;
}

/** The ledger GET payload, structurally typed: this module reads four arrays and ignores everything else. */
export interface LedgerPayload {
  entries?: Array<{ entry_id?: unknown; facts?: unknown }>;
  works?: Array<{ work_id?: unknown; status_history?: unknown; dissemination_events?: unknown }>;
  layout_facts?: Array<{ layout_fact_id?: unknown; evidence?: unknown }>;
}

export type EvidenceIndex = ReadonlyMap<string, ResolvedEvidence>;

export const EMPTY_EVIDENCE_INDEX: EvidenceIndex = new Map();

function str(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/**
 * The applicant text behind a fact, or null.
 *
 * ONLY TWO PROVENANCES CARRY APPLICANT TEXT. `extracted_span` carries the exact substring of the applicant's
 * document (§2.7 Form 1); `user_supplied` carries what the applicant typed. `closed_world` evidences an ABSENCE
 * (its scope is a claim about what was assessed, and §4.3 renders that through `CS-OMISSION-SCOPE`, never as a
 * quotation), and `derived` cites other facts rather than text. Neither has words to show, so neither resolves.
 *
 * EXPORTED IN R5.3 for `FactLedgerPanel` (Layer 1), which asks the same question — "does this fact have applicant
 * words to show?" — over the ledger's own `facts[]` rather than over an id citation. A second implementation of
 * the rule in the panel would be a second opinion about which provenances carry text.
 */
export function applicantTextOf(source: {
  provenance?: unknown;
  evidence_span?: { quoted_text?: unknown } | null;
  user_value?: unknown;
}): string | null {
  return applicantEvidenceOf(source)?.text ?? null;
}

/**
 * The same question, answered with WHICH KIND of applicant text it is (A11Y-3, round 3).
 *
 * A `'span'` is a clause lifted verbatim out of the document — it is the applicant's own sentence and reads as
 * one. A `'value'` is a stored field value: a correction the applicant typed into one field, or a normalized
 * token. The distinction is not cosmetic. The accessibility audit found a reveal that read out as "stated" nine
 * times, because nine §2.2a signal facts on one entry each stored the same value and nothing said which signal
 * each belonged to — so a value renders behind its registered field label and a span renders alone.
 *
 * IT IS RECORDED AT INDEX TIME, from the provenance, rather than inferred later from the text. "Does this look
 * like a sentence?" is not a decidable question, and a heuristic that got it wrong would either talk over the
 * applicant's own words or leave a bare token unlabelled.
 */
export function applicantEvidenceOf(source: {
  provenance?: unknown;
  evidence_span?: { quoted_text?: unknown } | null;
  user_value?: unknown;
}): { text: string; kind: 'span' | 'value' } | null {
  if (source.provenance === 'extracted_span') {
    const text = str(source.evidence_span?.quoted_text);
    return text === null ? null : { text, kind: 'span' };
  }
  if (source.provenance === 'user_supplied') {
    const text = str(source.user_value);
    return text === null ? null : { text, kind: 'value' };
  }
  return null;
}

/**
 * Build the id → evidence index from one ledger response.
 *
 * Defensive by construction rather than by assertion: every field is read through a narrowing helper, and a row
 * that does not yield a `(fact_id, text)` pair is skipped. A malformed ledger costs the surface its "show source"
 * affordances; it never costs it a render.
 */
export function buildEvidenceIndex(ledger: LedgerPayload | null | undefined): EvidenceIndex {
  const index = new Map<string, ResolvedEvidence>();
  if (!ledger || typeof ledger !== 'object') return index;

  const add = (
    fact_id: string | null, field: string, found: { text: string; kind: 'span' | 'value' } | null,
  ): void => {
    if (fact_id === null || found === null) return;
    if (!index.has(fact_id)) index.set(fact_id, { fact_id, field, quoted_text: found.text, kind: found.kind });
  };

  for (const entry of ledger.entries ?? []) {
    const facts = Array.isArray(entry?.facts) ? (entry.facts as LedgerFactLike[]) : [];
    for (const f of facts) {
      add(str(f?.fact_id), str(f?.field) ?? 'fact', applicantEvidenceOf(f ?? {}));
    }
  }

  for (const work of ledger.works ?? []) {
    const work_id = str(work?.work_id);
    if (work_id === null) continue;
    const statuses = Array.isArray(work?.status_history) ? (work.status_history as EvidenceCarrier[]) : [];
    statuses.forEach((s, i) => {
      add(`${work_id}:status:${i}`, 'status_history.status', applicantEvidenceOf(s?.evidence ?? {}));
    });
    const events = Array.isArray(work?.dissemination_events) ? (work.dissemination_events as EvidenceCarrier[]) : [];
    events.forEach((e, i) => {
      add(`${work_id}:event:${i}`, 'dissemination_events.event_type', applicantEvidenceOf(e?.evidence ?? {}));
    });
  }

  for (const lf of ledger.layout_facts ?? []) {
    const id = str(lf?.layout_fact_id);
    const ev = (lf?.evidence ?? {}) as { quoted_text?: unknown };
    // A layout fact's evidence is a bare `EvidenceSpan` (no `provenance` discriminator on the row itself) when it
    // quotes the document, and a derived/closed-world record otherwise. Only the quoting form has words to show —
    // and when it does, those words ARE a quoted span, so it is indexed as one.
    const quoted = str(ev?.quoted_text);
    add(id, 'layout', quoted === null ? null : { text: quoted, kind: 'span' });
  }

  return index;
}

/* ============================================================================================================== *
 * THE ENTRY HEADINGS (R5.7) — what an entry is CALLED everywhere §4 points at one.
 *
 * WHY THE LEDGER AND NOT THE PAYLOAD. `RubricReadPublicV1` carries no human label for an entry at all:
 * `EntryAssessment` is `entry_id` plus assessments (`rubricEngine.ts`), and §1.2a's add-list adds no title. The
 * only rendering of an entry pointer that resolves through one of T-COPY-1's six closed sources is therefore the
 * applicant's own words.
 *
 * THE FALLBACK IS DETERMINISTIC AND CLOSED: `role_title`, else `institution`, else `raw_text` — all three are
 * §12.6-enumerated `applicant_verbatim` fields, shown verbatim, exempt from the banned-output sweep exactly like a
 * quoted span. Nothing else is ever tried, and nothing is ever composed: a label is ONE field's value, never a
 * sentence this surface built out of several. The `not_stated` sentinel is the §2.1 marker the ledger API restores
 * for an unstated field — a marker, never a label — and is skipped at every rung.
 *
 * WHY `raw_text` IS A LEGITIMATE LAST RUNG. R5.2 labelled an entry by `role_title` or not at all, and the live
 * smoke (2026-08-22) found entries with no stated title rendering as anonymous facet strips, anonymous flags and
 * anonymous recommendation cards — three pointers at "this entry" with nothing to say WHICH. An entry's own raw
 * text is always present (it is the chunk the ledger was extracted FROM) and is the applicant's words; it is
 * long, which is a layout question (`EntryLabel` clamps it VISUALLY, never in JS), not a provenance one.
 *
 * THE DATES RIDE ALONG, as DATA: `date_start`/`date_end` are `YYYY`/`YYYY-MM` normalizations (§2.2) and render
 * through the registered typed-value templates (`CS-VALUE-DATE`, `CS-ENTRY-DATE-RANGE`), never through
 * `ApplicantText`, because a normalized partial is not the applicant's own words.
 * ============================================================================================================== */

/** Where the heading text came from — one of the three closed rungs, in fallback order. */
export type EntryHeadingSource = 'role_title' | 'institution' | 'raw_text';

export interface EntryHeading {
  /** The applicant's own words, verbatim — ONE field's value. */
  text: string;
  source: EntryHeadingSource;
  /** `YYYY` / `YYYY-MM` partials, or null when unstated. Rendered only through the registered date templates. */
  date_start: string | null;
  date_end: string | null;
}

/** The `not_stated` sentinel (§2.1) — restated here rather than imported so this module keeps zero imports of its own. */
const NOT_STATED_SENTINEL = 'not_stated';

function stated(v: unknown): string | null {
  const t = str(v);
  return t === null || t === NOT_STATED_SENTINEL ? null : t;
}

/**
 * The heading for ONE ledger entry, or null when not even `raw_text` is stated (a malformed row).
 *
 * Exported for `FactLedgerPanel`, which walks the ledger's own `entries[]` and can ask per entry without building a
 * map it already holds. The rule lives here ONCE — the panel and the map must never disagree about what an entry
 * is called.
 */
export function entryHeadingOf(entry: unknown): EntryHeading | null {
  const e = (entry ?? {}) as {
    role_title?: unknown; institution?: unknown; raw_text?: unknown; date_start?: unknown; date_end?: unknown;
  };
  const rungs: Array<[EntryHeadingSource, unknown]> = [
    ['role_title', e.role_title],
    ['institution', e.institution],
    ['raw_text', e.raw_text],
  ];
  for (const [source, v] of rungs) {
    const text = stated(v);
    if (text !== null) {
      return { text, source, date_start: stated(e.date_start), date_end: stated(e.date_end) };
    }
  }
  return null;
}

/** id → heading, over one ledger response. An entry with no id, or with nothing stated at any rung, gets no row. */
export function buildEntryHeadings(ledger: LedgerPayload | null | undefined): ReadonlyMap<string, EntryHeading> {
  const headings = new Map<string, EntryHeading>();
  for (const entry of ledger?.entries ?? []) {
    const id = str((entry as { entry_id?: unknown })?.entry_id);
    const heading = entryHeadingOf(entry);
    if (id !== null && heading !== null) headings.set(id, heading);
  }
  return headings;
}

/** The heading TEXT only, per entry — what a `{slot}` or a link label needs. */
export function labelsFromHeadings(headings: ReadonlyMap<string, EntryHeading>): ReadonlyMap<string, string> {
  const labels = new Map<string, string>();
  for (const [id, h] of headings) labels.set(id, h.text);
  return labels;
}

/**
 * THE ENTRY LABELS — the same rule, as a plain id → text map, for the call sites that need a string (a template
 * slot, a link's text). Kept as the R5.2 signature; since R5.7 it carries the full fallback rather than
 * `role_title` alone, so `DomainBands`' `{x}` and its qualifying-entry links name every entry the same way every
 * other layer does.
 */
export function buildEntryLabels(ledger: LedgerPayload | null | undefined): ReadonlyMap<string, string> {
  return labelsFromHeadings(buildEntryHeadings(ledger));
}

/** Resolve a citation list, dropping every id the ledger cannot evidence. Order follows the citation, not the map. */
export function resolveEvidence(index: EvidenceIndex, fact_ids: readonly string[] | null | undefined): ResolvedEvidence[] {
  if (!fact_ids) return [];
  const out: ResolvedEvidence[] = [];
  for (const id of fact_ids) {
    const hit = index.get(id);
    if (hit) out.push(hit);
  }
  return out;
}
